import React from 'react'
import { lodash } from 'src/utils'
import { classToFComponent, FCClassContext, Field, FieldHelp, FieldLabel, inputSx } from 'src/utils/views'
import { Logger } from 'wdc-cube'

import DeleteIcon from '@mui/icons-material/Delete'

import ClickAwayListener from '@mui/material/ClickAwayListener'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Popper from '@mui/material/Popper'
import TextField from '@mui/material/TextField'
import { ViewSlot } from 'wdc-cube-react'

import { Divider } from '@mui/material'
import {
    FilterAccordion,
    FilterAccordionDetails,
    FilterAccordionSummary
} from 'src/components/filteraccordion/filter_accordion'
import {
    JunctionMode,
    StringKeywordFilterScope,
    StringKeywordOperator,
    StringKeywordParcelFilterScope,
    type KeywordOption
} from '../../tcm_scopes'
import { DropdownBody, FieldIconButton, FilterPanel, criterionLabel } from 'src/utils/views/modules/the-consumer-market'

const LOG = Logger.get('StringFilterView')

type StringKeywordFilterViewProps = {
    className?: string
    scope: StringKeywordFilterScope
}

class StringKeywordFilterViewClass implements FCClassContext<StringKeywordFilterViewProps> {
    // :: Fields
    scope!: StringKeywordFilterScope

    onSyncState({ scope }: StringKeywordFilterViewProps) {
        this.scope = scope
    }

    render({ className }: StringKeywordFilterViewProps) {
        const { scope } = this

        return (
            <FilterAccordion className={className} expanded={scope.expanded} onChange={this.handleExpandedChange}>
                <FilterAccordionSummary
                    onClear={this.handleClearAllClicked}
                    onAdd={this.handlePlusClick}
                    caption={scope.caption}
                    captionBold={!!scope.conjunction.length || !!scope.disjunction.length}
                />
                <FilterAccordionDetails>
                    {!!scope.conjunction.length &&
                        scope.conjunction.map((parcelScope, idx, arr) => (
                            <StringKeywordParcelView
                                key={parcelScope.uid}
                                acceptBlankValues={scope.acceptBlankValues}
                                scope={parcelScope}
                                divider={idx < arr.length - 1 || !!scope.disjunction.length}
                            />
                        ))}

                    {!!scope.disjunction.length &&
                        scope.disjunction.map((parcelScope, idx, arr) => (
                            <StringKeywordParcelView
                                key={parcelScope.uid}
                                acceptBlankValues={scope.acceptBlankValues}
                                scope={parcelScope}
                                divider={idx < arr.length - 1}
                            />
                        ))}

                    {!scope.conjunction.length && !scope.disjunction.length && (
                        <FieldHelp>Nenhum critério adicionado. Use o + para incluir um.</FieldHelp>
                    )}
                </FilterAccordionDetails>
            </FilterAccordion>
        )
    } //

    readonly handleExpandedChange = (_event: React.SyntheticEvent, expanded: boolean) => {
        this.scope.onExpandedChange(expanded).catch(LOG.caught)
    }

    readonly handlePlusClick = () => {
        this.scope.onAddFilter(JunctionMode.OR).catch(LOG.caught)
    }

    readonly handleClearAllClicked = () => {
        this.scope.onClearAllFilters().catch(LOG.caught)
    }
}

export const StringKeywordFilterView = classToFComponent(StringKeywordFilterViewClass, React)
export default StringKeywordFilterView

// :: StringParcelViewClass

type StringKeywordParcelViewProps = {
    className?: string
    acceptBlankValues: boolean
    scope: StringKeywordParcelFilterScope
    divider?: boolean
}

class StringKeywordParcelViewClass implements FCClassContext<StringKeywordParcelViewProps> {
    scope!: StringKeywordParcelFilterScope
    multiline = false

    valueField = new (class {
        scope!: StringKeywordParcelFilterScope
        elm?: HTMLDivElement | null
        focused = false

        readonly setRef = (el: HTMLDivElement) => {
            this.elm = el
        }

        readonly handleFocus = () => {
            this.focused = true
            this.scope.update()
        }

        readonly handleBlur = () => {
            this.focused = false
            this.scope.update()
        }
    })()

    popper = new (class {
        scope!: StringKeywordParcelFilterScope
        open = false
    })()

    render({ className, acceptBlankValues, scope, divider }: StringKeywordParcelViewProps) {
        this.scope = scope
        this.valueField.scope = scope
        this.popper.scope = scope

        this.multiline =
            scope.operator === StringKeywordOperator.is_in || scope.operator === StringKeywordOperator.is_not_in

        if (scope.valueVisible && this.valueField.focused) {
            const searchText = scope.value.trim()
            this.popper.open = scope.options.length > 0 && searchText.length > 0
        } else if (scope.options.length === 0) {
            this.popper.open = false
        }

        return (
            <>
                <FilterPanel className={className}>
                    {lodash.isNotNil(scope.extra) && <ViewSlot scope={scope.extra} />}
                    <Field>
                        <FieldLabel htmlFor={scope.uid}>
                            {criterionLabel(scope.junctionMode === JunctionMode.AND)}
                        </FieldLabel>
                        <TextField
                            id={scope.uid}
                            select
                            size="small"
                            defaultValue={`${StringKeywordOperator.contains}`}
                            value={`${scope.operator}`}
                            onChange={this.handleSelectChange}
                            sx={inputSx}
                            fullWidth
                        >
                            <MenuItem value={StringKeywordOperator.contains}>Contém</MenuItem>
                            <MenuItem value={StringKeywordOperator.does_not_contain}>Não contém</MenuItem>
                            <MenuItem value={StringKeywordOperator.starts_with}>Começa com</MenuItem>
                            <MenuItem value={StringKeywordOperator.does_not_start_with}>Não começa com</MenuItem>
                            <MenuItem value={StringKeywordOperator.is}>É igual</MenuItem>
                            <MenuItem value={StringKeywordOperator.is_not}>Não é igual</MenuItem>
                            {acceptBlankValues && (
                                <MenuItem value={StringKeywordOperator.is_blank}>Está em branco</MenuItem>
                            )}
                            {acceptBlankValues && (
                                <MenuItem value={StringKeywordOperator.is_not_blank}>Não está em branco</MenuItem>
                            )}
                            <MenuItem value={StringKeywordOperator.is_in}>Está na lista</MenuItem>
                            <MenuItem value={StringKeywordOperator.is_not_in}>Não está na lista</MenuItem>
                        </TextField>
                    </Field>
                    {scope.valueVisible && (
                        <ClickAwayListener onClickAway={this.handleClickAway}>
                            <Field>
                                <FieldLabel htmlFor={`${scope.uid}-valor`}>Valor</FieldLabel>
                                <TextField
                                    id={`${scope.uid}-valor`}
                                    ref={this.valueField.setRef}
                                    size="small"
                                    value={scope.value}
                                    multiline={this.multiline}
                                    onChange={this.handleValueChange}
                                    onKeyDown={this.handleKeyDown}
                                    onFocus={this.valueField.handleFocus}
                                    onBlur={this.valueField.handleBlur}
                                    sx={inputSx}
                                    fullWidth
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <FieldIconButton
                                                    size="small"
                                                    aria-label="Limpar valor"
                                                    onClick={this.handleClearClick}
                                                >
                                                    <DeleteIcon />
                                                </FieldIconButton>
                                            )
                                        }
                                    }}
                                />
                                {this.multiline && (
                                    <FieldHelp>Um valor por linha. Ctrl+Enter aplica a lista.</FieldHelp>
                                )}
                                <Popper open={this.popper.open} anchorEl={this.valueField.elm} placement="bottom-start">
                                    <DropdownBody
                                        sx={{ width: this.valueField.elm ? this.valueField.elm.offsetWidth : 184 }}
                                    >
                                        <List dense={true}>
                                            {scope.options.map((option) => {
                                                return (
                                                    <ListItem key={option.key} disablePadding>
                                                        <ListItemButton
                                                            onClick={() => this.handleListItemClick(option)}
                                                        >
                                                            <ListItemText primary={option.label} />
                                                        </ListItemButton>
                                                    </ListItem>
                                                )
                                            })}
                                        </List>
                                    </DropdownBody>
                                </Popper>
                            </Field>
                        </ClickAwayListener>
                    )}
                </FilterPanel>
                {divider && <Divider sx={{ borderColor: 'var(--border)' }} />}
            </>
        )
    }

    readonly handleSelectChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onOperatorChange(+event.target.value)
    }

    readonly handleValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.scope.onValueChange(event.target.value).catch(LOG.caught)
    }

    readonly handleClearClick = () => {
        this.scope.onClear().catch(LOG.caught)
    }

    readonly handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
            if (this.multiline && !e.ctrlKey) {
                return
            }
            this.scope.onEnterKeyPressed().catch(LOG.caught)
        }
    }

    readonly handleClickAway = () => {
        this.popper.open = false
        this.scope.update()
    }

    readonly handleListItemClick = (option: KeywordOption): void => {
        this.scope.onOptionSelected(option).catch(LOG.caught)
    }
}

export const StringKeywordParcelView = classToFComponent(StringKeywordParcelViewClass, React)
